/**
 * Sanitizes incomplete markdown during streaming to prevent rendering artifacts.
 *
 * This sanitizer runs AFTER useSmooth (see markdown-text.tsx), so it processes
 * every animation frame. Each function REMOVES unpaired markers while keeping
 * the content visible. When the closing marker arrives, the content transitions
 * to its formatted state (e.g. text becomes bold).
 *
 * IMPORTANT: Do NOT use regex lookbehind assertions (?<= or ?<!) in this file.
 * They are not supported in Safari < 16.4 (iOS 16.0-16.3) and will cause:
 * "SyntaxError: Invalid regular expression: invalid group specifier name"
 */

function count(text: string, pattern: RegExp): number {
  return text.match(pattern)?.length ?? 0;
}

function isOdd(n: number): boolean {
  return n % 2 === 1;
}

export function sanitizeUnpairedBold(text: string): string {
  if (isOdd(count(text, /\*\*/g))) {
    return text.replace(/\*\*([^*]*)$/, '$1');
  }
  return text;
}

export function sanitizeUnpairedItalic(text: string): string {
  const opens = count(text, /(?:^|[\s(])\*[^\s*]/g);
  const closes = count(text, /[^\s*]\*(?:[\s).,!?]|$)/g);
  if (opens > closes) {
    const midMatch = text.match(/^([\s\S]*[\s(])\*[^\s*]/);
    if (midMatch) {
      return midMatch[1];
    }
    if (/^\*[^\s*]/.test(text)) {
      return text.slice(1);
    }
  }
  if (/[\s(]\*$/.test(text)) {
    return text.slice(0, -1);
  }
  return text;
}

export function sanitizeUnpairedStrikethrough(text: string): string {
  if (isOdd(count(text, /~~/g))) {
    return text.replace(/~~([^~]*)$/, '$1');
  }
  return text;
}

export function sanitizeUnpairedInlineCode(text: string): string {
  const singleBackticks = count(text, /`/g) - count(text, /```/g) * 3;
  if (isOdd(singleBackticks)) {
    return text.replace(/`([^`]*)$/, '$1');
  }
  return text;
}

export function encodeSpacesInLinkUrls(text: string): string {
  return text.replace(/\]\(([^)]+)\)/g, (match, url: string) => {
    if (!url.includes(' ')) return match;
    return `](${url.replace(/ /g, '%20')})`;
  });
}

export function sanitizeIncompleteLinks(text: string): string {
  let result = text;
  if (count(result, /\[/g) > count(result, /\]/g)) {
    result = result.replace(/\[[^\]]*$/, '');
  }
  return result.replace(/\]\([^)]*$/, ']');
}

export function convertHtmlBreaks(text: string): string {
  return text.replace(/<br\s*\/?>/gi, '\n');
}

export function sanitizeStreamingMarkdown(text: string): string {
  let result = text;
  result = convertHtmlBreaks(result);
  result = sanitizeUnpairedBold(result);
  result = sanitizeUnpairedItalic(result);
  result = sanitizeUnpairedStrikethrough(result);
  result = sanitizeUnpairedInlineCode(result);
  result = encodeSpacesInLinkUrls(result);
  result = sanitizeIncompleteLinks(result);
  return result;
}
